// As parcelas do "Personalizado" no ajuste mês a mês sobre a referência (design D21, spec
// target-month-adjustments): uma linha por mês da referência, com a competência (início +
// defasagem), o valor em R$ editável e o percentual. Ações por linha: adiar para o mês
// seguinte e remover. O saldo a redistribuir é a diferença entre as linhas e o valor do alvo.
//
// Só apresentação: as linhas e as contas são do editor (`referenceSchedule.js`).
import { useState } from "react";

import { competenceText, percentLabel } from "./format";
import { centsToReaisInput, parseReaisToCents, reais } from "./monthEdit";
import { WEIGHTED_MAX_LINES as MAX_LINES } from "./profile";
import { lineMonth } from "./referenceSchedule";

const bpsOf = (cents, totalCents) => (totalCents > 0 ? Math.round((cents * 10000) / totalCents) : 0);

const ReferenceInstallmentsTable = ({
  lines,
  startMonth,
  totalCents,
  balanceCents,
  editable = true,
  problem = null,
  onChangeCents,
  onPostpone,
  onRemove,
  onAdd,
  onBackToReference,
  backToReferenceDisabled = false,
}) => {
  // O texto em digitação de cada linha: o campo não reformata "12," enquanto o cliente digita.
  const [drafts, setDrafts] = useState({});
  const clearDrafts = () => setDrafts({});
  return (
    <div className="ampli-month-edit">
      <div
        className="ampli-rule-projection ampli-month-edit__table"
        role="region"
        aria-label="Parcelas do ajuste"
        tabIndex={0}
      >
        <table>
          <caption className="visually-hidden">Parcelas do ajuste sobre a referência</caption>
          <thead>
            <tr>
              <th scope="col">Parcela</th>
              <th scope="col">Competência</th>
              <th scope="col" className="num">Valor</th>
              <th scope="col" className="num">Percentual</th>
              {editable ? (
                <th scope="col">
                  <span className="visually-hidden">Ações</span>
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => {
              const month = lineMonth(startMonth, line);
              const label = month ? competenceText(month) : `parcela ${index + 1}`;
              const draft = drafts[line.lineId];
              return (
                <tr key={line.lineId} data-testid={`reference-line-${month ?? line.offset}`}>
                  <td>{`${index + 1}ª`}</td>
                  <th scope="row">{month ? competenceText(month) : "—"}</th>
                  <td className="num">
                    {editable ? (
                      <input
                        type="text"
                        inputMode="decimal"
                        className="default-input ampli-month-edit__input"
                        aria-label={`Valor em ${label}`}
                        value={draft ?? centsToReaisInput(line.cents)}
                        aria-invalid={draft !== undefined && Number.isNaN(parseReaisToCents(draft))}
                        onChange={(event) => {
                          const text = event.target.value;
                          setDrafts((current) => ({ ...current, [line.lineId]: text }));
                          const cents = parseReaisToCents(text);
                          if (Number.isSafeInteger(cents) && cents >= 0) onChangeCents(line.lineId, cents);
                        }}
                        onBlur={() => setDrafts(({ [line.lineId]: _left, ...rest }) => rest)}
                      />
                    ) : reais(line.cents)}
                  </td>
                  <td className="num">{percentLabel(bpsOf(line.cents, totalCents))}</td>
                  {editable ? (
                    <td className="ampli-month-edit__actions">
                      <button
                        type="button"
                        className="button button-ghost small"
                        disabled={!(line.cents > 0)}
                        aria-label={`Adiar ${label} para o mês seguinte`}
                        onClick={() => {
                          clearDrafts();
                          onPostpone(line.lineId);
                        }}
                      >
                        Adiar para o mês seguinte
                      </button>
                      <button
                        type="button"
                        className="button button-ghost small"
                        aria-label={`Remover a parcela de ${label}`}
                        onClick={() => {
                          clearDrafts();
                          onRemove(line.lineId);
                        }}
                      >
                        Remover
                      </button>
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th scope="row" colSpan={2}>Total</th>
              <td className="num">{reais(totalCents + balanceCents)}</td>
              <td className="num">{percentLabel(bpsOf(totalCents + balanceCents, totalCents))}</td>
              {editable ? <td /> : null}
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="ampli-month-edit__footer">
        <p
          className={`ampli-month-edit__balance${balanceCents !== 0 ? " is-open" : ""}`}
          role="status"
          data-testid="reference-balance"
        >
          {balanceCents === 0
            ? "Saldo a redistribuir: R$ 0,00"
            : balanceCents < 0
              ? `Saldo a redistribuir: ${reais(-balanceCents)}; coloque em alguma parcela`
              : `As parcelas passam do valor do alvo em ${reais(balanceCents)}; tire de alguma parcela`}
        </p>
        {editable ? (
          <div className="ampli-month-edit__tools">
            <button
              type="button"
              className="button button-ghost small"
              disabled={lines.length >= MAX_LINES}
              onClick={() => {
                clearDrafts();
                onAdd();
              }}
            >
              Adicionar parcela
            </button>
            <button
              type="button"
              className="button button-ghost small"
              disabled={backToReferenceDisabled}
              onClick={() => {
                clearDrafts();
                onBackToReference();
              }}
            >
              Voltar à referência
            </button>
          </div>
        ) : null}
      </div>
      {problem ? (
        <p className="alert alert-error" role="alert">{problem}</p>
      ) : null}
    </div>
  );
};

export default ReferenceInstallmentsTable;
